import * as React from "react"
import parse from "html-react-parser"
import Fuse from "fuse.js"
import { graphql } from "gatsby"
import { Layout } from "@bw/layouts"
import { PageHeader, Columns } from "@bw/modules"
import { Section, Seo } from "@bw/bits"
import { Teaser } from "@bw/partials"
import { useTranslation } from "react-i18next"
import { FormField, Input } from "@bw/forms"
import { Formik, Form, Field } from "formik"
import { toast } from "react-toastify"
import * as Icons from "@bw/icons"

const initialValues = {
  search: "",
}

let timer

const SearchPage = ({ data, pageContext }) => {
  const { t } = useTranslation()
  const [filter, setFilter] = React.useState(initialValues)

  const documents = React.useMemo(() => {
    const { articles, pages, prestations, pharmacies, jobs, magazines } = data
    return [
      ...articles.nodes,
      ...pages.nodes,
      ...prestations.nodes,
      ...pharmacies.nodes,
      ...jobs.nodes,
      ...magazines.nodes,
    ]
  }, [data])

  const fuse = React.useMemo(() => {
    if (!documents) {
      return null
    }

    return new Fuse(documents, {
      keys: [
        {
          name: "title",
          weight: 3,
        },
      ],
      minMatchCharLength: 3,
      ignoreLocation: true,
      threshold: 0.2,
    })
  }, [documents])

  const results = React.useMemo(() => {
    if (!fuse || filter.search.length < 3) {
      return []
    }

    const found = fuse.search(filter.search)

    if (found.length === 0) {
      toast.error(t("toast.noResult"))
    }

    return found.map(i => i.item)
  }, [filter, t, fuse])

  return (
    <Layout color="green" {...{ pageContext }}>
      <Seo
        title="recherche"
        meta={[
          {
            name: "robots",
            content: "noindex, nofollow",
          },
        ]}
      />
      <PageHeader
        pictogram={
          <svg className="icon">
            <use xlinkHref="/sprite-v2.svg#cross" />
          </svg>
        }
        title={t("search.title")}
      />
      <Section>
        <Columns count={3} gap="xxlarge">
          <Formik
            initialValues={initialValues}
            onSubmit={values => {
              setFilter({
                ...filter,
                ...values,
              })
            }}
          >
            {({ submitForm }) => (
              <Form
                onChange={() => {
                  clearTimeout(timer)
                  timer = setTimeout(submitForm, 500)
                }}
              >
                <FormField title={t("search.searchLabel")} name="search">
                  <Field
                    name="search"
                    component={Input}
                    autoComplete="off"
                    icon={<Icons.Search />}
                  />
                </FormField>
              </Form>
            )}
          </Formik>
        </Columns>
      </Section>
      <Section>
        {results.length > 0 ? (
          <Columns count={3} gap="xlarge">
            {results.map((item, i) => (
              <Teaser
                key={i}
                suptitle={t(`types.${item.__typename}Singular`)}
                title={item.title}
                excerpt={item?.teaserExcerpt}
                image={
                  <img
                    src={
                      item.teaserImage?.url ||
                      (item.images && item.images[0]?.url) ||
                      "/placeholder-teaser.jpg"
                    }
                    alt=""
                  />
                }
                to={item.slug}
              />
            ))}
          </Columns>
        ) : (
          <h3 style={{ fontWeight: 300 }}>
            {parse(t("search.searchIn", { count: documents.length || 1 }))}
          </h3>
        )}
      </Section>
    </Layout>
  )
}

export const pageSearchQuery = graphql`
  query searchQuery {
    pages: allCollectionPages {
      nodes {
        __typename
        title
        slug
        teaserExcerpt
        teaserImage {
          url
        }
      }
    }
    articles: allCollectionArticles {
      nodes {
        __typename
        title
        slug
        teaserExcerpt
        teaserImage {
          url
        }
      }
    }
    prestations: allCollectionPrestations {
      nodes {
        __typename
        title
        slug
        teaserExcerpt
        teaserImage {
          url
        }
      }
    }
    pharmacies: allCollectionPharmacies {
      nodes {
        __typename
        title
        slug
        teaserExcerpt: city
        images {
          url
        }
      }
    }
    jobs: allCollectionJobs {
      nodes {
        __typename
        title
        slug
        teaserExcerpt: occupancyRate
      }
    }
    magazines: allCollectionMagazines {
      nodes {
        __typename
        title
        slug
        teaserImage: cover {
          url
        }
      }
    }
  }
`

export default SearchPage
